import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', this.#onDocumentClick, {
      signal: this.#abortController.signal,
    });

    window.addEventListener('scroll', this.#onWindowScroll, {
      signal: this.#abortController.signal,
      passive: true,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #state = {
    activeItem: null,
  };

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]');
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Called by the Component framework when on:click="/activate" fires.
   */
  activate = (event) => {
    // Safety net: if the click originated from within a mega-menu element,
    // do nothing. The capture-phase listener in the inline script handles
    // tab/panel switching and prevents propagation, but check here too
    // in case the framework passes the original event with the real target.
    if (event.target instanceof Element) {
      const megaEl = event.target.closest('[data-mega-menu-tab], .mega-menu__panel, .mega-menu__tab-link--direct, .mega-menu__wrapper');
      if (megaEl) return;
    }
    // Also check composedPath for shadow DOM scenarios
    if (typeof event.composedPath === 'function') {
      const path = event.composedPath();
      for (let i = 0; i < path.length; i++) {
        const el = path[i];
        if (el instanceof Element && el.matches('[data-mega-menu-tab], .mega-menu__panel, .mega-menu__wrapper')) {
          return;
        }
      }
    }

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Prevent <a> default navigation for items with submenus
    if (item && item.getAttribute('aria-haspopup') === 'true') {
      event.preventDefault();
    }

    // Toggle: clicking the already-active item closes it
    if (item && item === this.#state.activeItem) {
      this.#deactivate(item);
      return;
    }

    this.#debouncedActivateHandler(event);
  };

  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    // Reset mega-menu tab/panel state every time a new submenu opens
    this.#resetMegaMenuState();
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    // Fix: blur focused element to clear :focus-within state,
    // so menu item colors reset properly
    if (document.activeElement && this.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    this.#resetMegaMenuState();

    setTimeout(() => {
      item.removeAttribute('data-animating');
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150));
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #onDocumentClick = (event) => {
    if (!this.#state.activeItem) return;
    if (this.contains(event.target)) return;
    this.#deactivate();
  };

  #onWindowScroll = () => {
    if (!this.#state.activeItem) return;
    // Don't close when mouse is inside the menu (scroll chaining from panels)
    if (this.matches(':hover')) return;
    this.#deactivate();
  };

  #resetMegaMenuState = () => {
    const wrappers = this.querySelectorAll('.mega-menu__wrapper');
    wrappers.forEach((w) => {
      w.querySelectorAll('.mega-menu__tab').forEach((t) => t.classList.remove('is-active'));
      w.querySelectorAll('[data-mega-menu-tab]').forEach((t) => t.setAttribute('aria-selected', 'false'));
      w.querySelectorAll('.mega-menu__panel').forEach((p) => {
        p.classList.remove('is-active');
        p.setAttribute('hidden', '');
      });
      const panels = w.querySelector('.mega-menu__panels');
      if (panels) {
        panels.scrollTop = 0; // must reset BEFORE display:none
        panels.classList.remove('is-visible');
      }
    });
  };

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
